import { computed, ref, watch } from 'vue';
import BroadcastsApi from 'dashboard/api/broadcasts';

const defaultMessage = () => ({
  content: '',
  content_type: 'text',
  private: false,
  content_attributes: { silent: true },
});

const defaultOptions = () => ({ throttle_per_second: 20 });

const clone = value => JSON.parse(JSON.stringify(value));

const editableConversationFilterOrder = {
  courier_apps: ['boltfood', 'ubereats', 'glovo', 'wolt', 'stuart'],
  languages: ['en', 'ru', 'pl', 'uk'],
};

const normalizeConversationFilters = filters => {
  const normalizedFilters = clone(filters || {});

  Object.entries(editableConversationFilterOrder).forEach(([key, order]) => {
    const selectedValues = new Set(normalizedFilters[key] || []);
    const normalizedValues = order.filter(value => selectedValues.has(value));

    if (normalizedValues.length) {
      normalizedFilters[key] = normalizedValues;
    } else {
      delete normalizedFilters[key];
    }
  });

  return normalizedFilters;
};

const parseIdentifierValues = rawText =>
  rawText.split(/\r\n|\n|\r/).filter(value => value.trim().length > 0);

export function useBroadcastComposer(blacklistVersion = ref(0)) {
  const title = ref('');
  const message = ref(defaultMessage());
  const options = ref(defaultOptions());
  const inboxId = ref('');
  const conversationFilters = ref({});
  const sources = ref([]);
  const preview = ref(null);
  const previewSignature = ref('');
  const templates = ref([]);
  const selectedTemplateId = ref('');
  const templateName = ref('');
  const isPreviewing = ref(false);
  const isLaunching = ref(false);
  const isLoadingTemplates = ref(false);
  const isSavingTemplate = ref(false);
  const isDeletingTemplate = ref(false);
  let nextSourceId = 1;

  const nextSourceKey = () => {
    const existingKeys = new Set(sources.value.map(source => source.key));
    let key = `broadcast-source-${nextSourceId}`;
    while (existingKeys.has(key)) {
      nextSourceId += 1;
      key = `broadcast-source-${nextSourceId}`;
    }
    nextSourceId += 1;
    return key;
  };

  const buildTargetSpec = () => ({
    version: 1,
    inbox_id: Number(inboxId.value),
    conversation_filters: normalizeConversationFilters(
      conversationFilters.value
    ),
    sources: sources.value.map(source => {
      const serializedSource = {
        key: source.key,
        type: source.type,
        operation: source.operation,
      };

      if (source.type === 'team') {
        return {
          ...serializedSource,
          team_ids: [...source.teamIds],
        };
      }

      const identifierSource = {
        ...serializedSource,
        identifier_type: source.identifierType,
        values: parseIdentifierValues(source.rawText),
      };

      if (source.teamIds.length) {
        identifierSource.team_ids = [...source.teamIds];
      }

      return identifierSource;
    }),
  });

  const buildMessage = () => clone(message.value);
  const buildOptions = () => ({
    ...clone(options.value),
    throttle_per_second: Number(options.value.throttle_per_second) || 20,
  });

  const currentSignature = computed(() =>
    JSON.stringify({
      title: title.value,
      message: buildMessage(),
      options: buildOptions(),
      target_spec: buildTargetSpec(),
      blacklist_version: blacklistVersion.value,
    })
  );

  watch(currentSignature, newSignature => {
    if (previewSignature.value && previewSignature.value !== newSignature) {
      preview.value = null;
      previewSignature.value = '';
    }
  });

  const identifierResolutionSignature = source =>
    JSON.stringify({
      blacklist_version: blacklistVersion.value,
      inbox_id: Number(inboxId.value),
      identifier_type: source.identifierType,
      raw_text: source.rawText,
      team_ids: source.teamIds,
      conversation_filters: normalizeConversationFilters(
        conversationFilters.value
      ),
    });

  const isIdentifierSourceConfirmed = source =>
    source.type !== 'identifiers' ||
    (source.resolution &&
      source.confirmedSignature === identifierResolutionSignature(source));

  const hasValidSources = computed(
    () =>
      sources.value.length > 0 &&
      sources.value.some(source => source.operation === 'include') &&
      sources.value.every(source => {
        if (source.type === 'team') return source.teamIds.length > 0;
        return parseIdentifierValues(source.rawText).length > 0;
      })
  );

  const canPreview = computed(
    () =>
      Number(inboxId.value) > 0 &&
      hasValidSources.value &&
      sources.value.every(isIdentifierSourceConfirmed) &&
      !isPreviewing.value
  );

  const canLaunch = computed(
    () =>
      title.value.trim().length > 0 &&
      message.value.content?.trim().length > 0 &&
      preview.value?.summary?.total_targets > 0 &&
      previewSignature.value === currentSignature.value &&
      !isLaunching.value
  );

  const canSaveTemplate = computed(
    () =>
      templateName.value.trim().length > 0 &&
      Number(inboxId.value) > 0 &&
      hasValidSources.value &&
      !isSavingTemplate.value
  );

  const addTeamSource = () => {
    sources.value.push({
      key: nextSourceKey(),
      type: 'team',
      operation: 'include',
      teamIds: [],
    });
  };

  const addIdentifierSource = () => {
    sources.value.push({
      key: nextSourceKey(),
      type: 'identifiers',
      operation: 'include',
      identifierType: 'telegram_id',
      rawText: '',
      teamIds: [],
      resolution: null,
      confirmedSignature: '',
      isResolving: false,
    });
  };

  const updateConversationFilter = (filterName, values) => {
    conversationFilters.value = normalizeConversationFilters({
      ...clone(conversationFilters.value),
      [filterName]: values,
    });

    sources.value.forEach(source => {
      if (source.type !== 'identifiers') return;
      source.resolution = null;
      source.confirmedSignature = '';
    });
  };

  const updateSource = (sourceIndex, source) => {
    sources.value.splice(sourceIndex, 1, source);
  };

  const removeSource = sourceIndex => {
    sources.value.splice(sourceIndex, 1);
  };

  const moveSource = (sourceIndex, direction) => {
    const targetIndex = sourceIndex + direction;
    if (targetIndex < 0 || targetIndex >= sources.value.length) return;
    const [source] = sources.value.splice(sourceIndex, 1);
    sources.value.splice(targetIndex, 0, source);
  };

  const confirmIdentifierSource = async source => {
    source.isResolving = true;
    try {
      const confirmationSignature = identifierResolutionSignature(source);
      const response = await BroadcastsApi.resolveTarget({
        inbox_id: Number(inboxId.value),
        identifier_type: source.identifierType,
        raw_text: source.rawText,
        team_ids: [...source.teamIds],
        conversation_filters: normalizeConversationFilters(
          conversationFilters.value
        ),
      });
      if (confirmationSignature !== identifierResolutionSignature(source)) {
        return null;
      }
      source.resolution = response.data;
      source.confirmedSignature = confirmationSignature;
      return response.data;
    } finally {
      source.isResolving = false;
    }
  };

  const previewTargets = async () => {
    isPreviewing.value = true;
    preview.value = null;
    previewSignature.value = '';
    try {
      const signature = currentSignature.value;
      const response = await BroadcastsApi.previewTargets(buildTargetSpec());
      if (signature !== currentSignature.value) return null;
      preview.value = response.data;
      previewSignature.value = signature;

      response.data.sources.forEach(sourceResult => {
        if (!sourceResult.identifier_resolution) return;
        const source = sources.value.find(
          item => item.key === sourceResult.key
        );
        if (!source) return;
        source.resolution = sourceResult.identifier_resolution;
        source.confirmedSignature = identifierResolutionSignature(source);
      });

      return response.data;
    } finally {
      isPreviewing.value = false;
    }
  };

  const fetchTemplates = async () => {
    isLoadingTemplates.value = true;
    try {
      const response = await BroadcastsApi.getTemplates();
      templates.value = response.data;
      return response.data;
    } finally {
      isLoadingTemplates.value = false;
    }
  };

  const applyTemplate = template => {
    const targetSpec = template.target_spec;
    inboxId.value = targetSpec.inbox_id;
    conversationFilters.value = normalizeConversationFilters(
      targetSpec.conversation_filters
    );
    sources.value = targetSpec.sources.map(source => ({
      key: source.key,
      type: source.type,
      operation: source.operation,
      teamIds: [...(source.team_ids || [])],
      ...(source.type === 'identifiers'
        ? {
            identifierType: source.identifier_type,
            rawText: source.values.join('\n'),
            resolution: null,
            confirmedSignature: '',
            isResolving: false,
          }
        : {}),
    }));
    message.value = {
      ...defaultMessage(),
      ...clone(template.message_payload || {}),
      content_attributes: {
        ...defaultMessage().content_attributes,
        ...(template.message_payload?.content_attributes || {}),
      },
    };
    options.value = {
      ...defaultOptions(),
      ...clone(template.options || {}),
    };
    templateName.value = template.name;
    title.value = template.name;
    preview.value = null;
    previewSignature.value = '';
  };

  const loadTemplate = async templateId => {
    const response = await BroadcastsApi.getTemplate(templateId);
    selectedTemplateId.value = response.data.id;
    applyTemplate(response.data);
    return response.data;
  };

  const templatePayload = () => ({
    broadcast_template: {
      name: templateName.value.trim(),
      target_spec: buildTargetSpec(),
      message: buildMessage(),
      options: buildOptions(),
    },
  });

  const saveTemplate = async () => {
    isSavingTemplate.value = true;
    try {
      const response = await BroadcastsApi.createTemplate(templatePayload());
      await fetchTemplates();
      selectedTemplateId.value = response.data.id;
      return response.data;
    } finally {
      isSavingTemplate.value = false;
    }
  };

  const updateTemplate = async () => {
    isSavingTemplate.value = true;
    try {
      const response = await BroadcastsApi.updateTemplate(
        selectedTemplateId.value,
        templatePayload()
      );
      await fetchTemplates();
      return response.data;
    } finally {
      isSavingTemplate.value = false;
    }
  };

  const deleteTemplate = async () => {
    isDeletingTemplate.value = true;
    try {
      await BroadcastsApi.deleteTemplate(selectedTemplateId.value);
      selectedTemplateId.value = '';
      templateName.value = '';
      await fetchTemplates();
    } finally {
      isDeletingTemplate.value = false;
    }
  };

  const launchBroadcast = async () => {
    isLaunching.value = true;
    try {
      return await BroadcastsApi.create({
        broadcast: {
          title: title.value.trim(),
          message: buildMessage(),
          target_spec: buildTargetSpec(),
          options: buildOptions(),
        },
      });
    } finally {
      isLaunching.value = false;
    }
  };

  const resetComposer = () => {
    title.value = '';
    message.value = defaultMessage();
    options.value = defaultOptions();
    inboxId.value = '';
    conversationFilters.value = {};
    sources.value = [];
    preview.value = null;
    previewSignature.value = '';
    selectedTemplateId.value = '';
    templateName.value = '';
  };

  return {
    title,
    message,
    options,
    inboxId,
    conversationFilters,
    sources,
    preview,
    templates,
    selectedTemplateId,
    templateName,
    isPreviewing,
    isLaunching,
    isLoadingTemplates,
    isSavingTemplate,
    isDeletingTemplate,
    canPreview,
    canLaunch,
    canSaveTemplate,
    addTeamSource,
    addIdentifierSource,
    updateConversationFilter,
    updateSource,
    removeSource,
    moveSource,
    confirmIdentifierSource,
    previewTargets,
    fetchTemplates,
    loadTemplate,
    saveTemplate,
    updateTemplate,
    deleteTemplate,
    launchBroadcast,
    resetComposer,
  };
}
